import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import {
  Users, TrendingUp, TrendingDown, Phone, MapPin, Clock, Calendar,
  Search, Download, ChevronRight, Star, Package, BarChart3, PieChart,
  UserCheck, UserX, UserPlus, RefreshCw, Filter, ArrowUpDown, Eye,
  MessageCircle, Target, Zap, Award, ShoppingBag, DollarSign, Activity,
  FileText, Save, History, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RePieChart, Pie, Cell, Legend, Area, AreaChart
} from 'recharts';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Follow-up status configuration
const FOLLOW_UP_STATUSES = [
  { value: 'sin_contactar', label: 'Sin Contactar', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
  { value: 'contactado', label: 'Contactado', color: 'bg-blue-100 text-blue-700', icon: Phone },
  { value: 'interesado', label: 'Interesado', color: 'bg-yellow-100 text-yellow-700', icon: Star },
  { value: 'pedido_realizado', label: 'Pedido Realizado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'no_interesado', label: 'No Interesado', color: 'bg-red-100 text-red-700', icon: XCircle }
];

// StatCard component (outside main component to prevent re-creation on render)
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card className={`bg-gradient-to-br ${color} text-white overflow-hidden relative`}>
    <CardContent className="p-5">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/80 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="bg-white/20 p-2.5 rounded-xl">
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-200' : 'text-red-200'}`}>
          {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{Math.abs(trend)}% vs semana anterior</span>
        </div>
      )}
    </CardContent>
    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
  </Card>
);

const DeliveryCRM = () => {
  const { api } = useAuth();
  const { selectedBranch, isMatriz } = useBranch();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [productAnalytics, setProductAnalytics] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [reactivation, setReactivation] = useState(null);
  
  // New Customer Modal
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', address: '', colonia: '', birthday: '', notes: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Filters
  const [dateFilter, setDateFilter] = useState('year'); // Default to year to show all customers including new ones
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [followUpFilter, setFollowUpFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all'); // Filter by order origin
  const [lastPurchaseFilter, setLastPurchaseFilter] = useState('all'); // NEW: Filter by last purchase recency
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('last_visit');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Calculate days based on dateFilter - memoized inline
  const days = (() => {
    switch(dateFilter) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      case 'year': return 365;
      case 'custom': return 365;
      default: return 30;
    }
  })();
  
  // Follow-up editing state
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [followUpStatus, setFollowUpStatus] = useState('sin_contactar');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (!isMatriz && selectedBranch) {
        params.append('branch_id', selectedBranch);
        params.append('is_matriz', 'false');
      } else {
        params.append('is_matriz', 'true');
      }
      const res = await api.get(`/crm/dashboard?${params}`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [api, days, selectedBranch, isMatriz]);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        filter_type: customerFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: '100'
      });
      
      // Handle date filtering
      if (dateFilter === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else {
        params.append('days', days.toString());
      }
      
      if (searchQuery) params.append('search', searchQuery);
      if (followUpFilter && followUpFilter !== 'all') params.append('follow_up_filter', followUpFilter);
      if (originFilter && originFilter !== 'all') params.append('origin_filter', originFilter);
      if (lastPurchaseFilter && lastPurchaseFilter !== 'all') params.append('last_purchase_filter', lastPurchaseFilter);
      if (!isMatriz && selectedBranch) {
        params.append('branch_id', selectedBranch);
        params.append('is_matriz', 'false');
      } else {
        params.append('is_matriz', 'true');
      }
      const res = await api.get(`/crm/customers?${params}`);
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [api, customerFilter, followUpFilter, originFilter, lastPurchaseFilter, dateFilter, days, customStartDate, customEndDate, sortBy, sortOrder, searchQuery, selectedBranch, isMatriz]);

  const fetchProductAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (!isMatriz && selectedBranch) {
        params.append('branch_id', selectedBranch);
        params.append('is_matriz', 'false');
      } else {
        params.append('is_matriz', 'true');
      }
      const res = await api.get(`/crm/products/analytics?${params}`);
      setProductAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching product analytics:', error);
    }
  }, [api, days, selectedBranch, isMatriz]);

  const fetchHourlyData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (!isMatriz && selectedBranch) {
        params.append('branch_id', selectedBranch);
        params.append('is_matriz', 'false');
      } else {
        params.append('is_matriz', 'true');
      }
      const res = await api.get(`/crm/analytics/hourly?${params}`);
      setHourlyData(res.data);
    } catch (error) {
      console.error('Error fetching hourly data:', error);
    }
  }, [api, days, selectedBranch, isMatriz]);

  const fetchReactivation = useCallback(async () => {
    try {
      const res = await api.get('/crm/reactivation?days_inactive=30&limit=20');
      setReactivation(res.data);
    } catch (error) {
      console.error('Error fetching reactivation:', error);
    }
  }, [api]);

  const fetchCustomerDetail = async (customerId) => {
    try {
      const res = await api.get(`/crm/customers/${customerId}`);
      setCustomerDetail(res.data);
      // Initialize follow-up editing state
      setFollowUpStatus(res.data.follow_up_status || 'sin_contactar');
      setFollowUpNotes(res.data.follow_up_notes || '');
      setEditingFollowUp(false);
    } catch (error) {
      toast.error('Error al cargar detalle del cliente');
    }
  };

  const handleSaveFollowUp = async () => {
    if (!customerDetail) return;
    
    setSavingFollowUp(true);
    try {
      await api.put(`/crm/customers/${customerDetail.customer_id}/follow-up`, {
        follow_up_status: followUpStatus,
        follow_up_notes: followUpNotes
      });
      
      toast.success('Seguimiento guardado');
      setEditingFollowUp(false);
      
      // Refresh customer detail to get updated history
      await fetchCustomerDetail(customerDetail.customer_id);
      // Also refresh customer list
      fetchCustomers();
    } catch (error) {
      toast.error('Error al guardar seguimiento');
    } finally {
      setSavingFollowUp(false);
    }
  };

  // Create new customer
  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim() || !newCustomer.phone.trim()) {
      toast.error('Nombre y teléfono son requeridos');
      return;
    }
    
    setSavingCustomer(true);
    try {
      const res = await api.post('/crm/customers', newCustomer);
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowNewCustomerModal(false);
        setNewCustomer({ name: '', phone: '', address: '', colonia: '', birthday: '', notes: '' });
        fetchCustomers(); // Refresh list
      } else {
        toast.error(res.data.message || 'Error al crear cliente');
      }
    } catch (error) {
      toast.error('Error al crear cliente');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generando PDF...');
      const params = new URLSearchParams({
        filter_type: customerFilter,
        days: days.toString()
      });
      // Include all active filters
      if (followUpFilter && followUpFilter !== 'all') params.append('follow_up_filter', followUpFilter);
      if (originFilter && originFilter !== 'all') params.append('origin_filter', originFilter);
      if (lastPurchaseFilter && lastPurchaseFilter !== 'all') params.append('last_purchase_filter', lastPurchaseFilter);
      if (dateFilter === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      }
      
      const response = await api.get(`/crm-reports/customers-pdf?${params}`, { responseType: 'blob' });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `clientes_crm_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  const openWhatsApp = async (phone, customerName, customerId = null) => {
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    // Clean phone number
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(`¡Hola ${customerName}! 👋 Te contactamos de Mr. Frogs. ¿Cómo podemos ayudarte hoy?`);
    window.open(`https://wa.me/52${cleanPhone}?text=${message}`, '_blank');
    
    // Register in CRM
    try {
      await api.post('/communications', {
        customer_id: customerId || `delivery_${cleanPhone}`,
        customer_name: customerName,
        customer_phone: cleanPhone,
        message_type: 'custom',
        channel: 'whatsapp',
        message_content: 'Contacto desde CRM Domicilio'
      });
    } catch (error) {
      console.error('Error registering communication:', error);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        filter_type: customerFilter,
        days: days.toString()
      });
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/crm/export/customers?${params}`, '_blank');
      toast.success('Exportación iniciada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchCustomers(),
        fetchProductAnalytics(),
        fetchHourlyData(),
        fetchReactivation()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchCustomers, fetchProductAnalytics, fetchHourlyData, fetchReactivation]);

  // Re-fetch customers when ANY filter changes
  useEffect(() => {
    if (!loading) {
      fetchCustomers();
    }
  }, [dateFilter, days, customStartDate, customEndDate, customerFilter, followUpFilter, originFilter, lastPurchaseFilter, searchQuery, sortBy, sortOrder]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex">
      <Sidebar />

      <main className="flex-1 md:ml-64 p-4 md:p-6 pt-16 md:pt-6">
        <div className="max-w-7xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="bg-blue-600 p-2 rounded-xl">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  CRM Domicilio
                </h1>
                <p className="text-gray-600 mt-1">Analíticas y estrategias para aumentar ventas a domicilio</p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { value: '7d', label: '7d' },
                    { value: '30d', label: '30d' },
                    { value: '90d', label: '90d' },
                    { value: 'year', label: 'Año' }
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      variant={dateFilter === opt.value ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => { setDateFilter(opt.value); setShowCustomDates(false); }}
                      className="text-xs px-2"
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <Button variant="outline" onClick={() => { fetchDashboard(); fetchCustomers(); fetchProductAnalytics(); fetchHourlyData(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-xl p-1.5 shadow-sm" data-testid="crm-tabs">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'customers', label: 'Clientes', icon: Users },
              { id: 'products', label: 'Productos', icon: Package },
              { id: 'timing', label: 'Horarios', icon: Clock },
              { id: 'reactivation', label: 'Reactivación', icon: Zap }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`crm-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              {/* DASHBOARD TAB */}
              <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }} data-testid="crm-dashboard-tab">
                {dashboard && (
                  <div className="space-y-6">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="crm-kpi-cards">
                      <StatCard
                        title="Total Clientes"
                        value={dashboard.total_customers}
                        subtitle={`${dashboard.active_customers} activos`}
                        icon={Users}
                        color="from-blue-500 to-blue-600"
                      />
                      <StatCard
                        title="Pedidos Domicilio"
                        value={dashboard.total_orders}
                        subtitle={`$${dashboard.total_revenue?.toLocaleString()}`}
                        icon={ShoppingBag}
                        color="from-green-500 to-green-600"
                        trend={dashboard.growth_rate}
                      />
                      <StatCard
                        title="Ticket Promedio"
                        value={`$${dashboard.avg_ticket?.toFixed(0)}`}
                        subtitle="Por pedido"
                        icon={DollarSign}
                        color="from-purple-500 to-purple-600"
                      />
                      <StatCard
                        title="Clientes Frecuentes"
                        value={dashboard.frequent_customers}
                        subtitle={`${dashboard.occasional_customers} ocasionales`}
                        icon={Award}
                        color="from-amber-500 to-orange-500"
                      />
                    </div>

                    {/* Segmentation & Top Spenders */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Customer Segmentation */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <PieChart className="w-5 h-5 text-blue-600" />
                            Segmentación de Clientes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <RePieChart>
                                <Pie
                                  data={[
                                    { name: 'Frecuentes (5+)', value: dashboard.frequent_customers, color: '#10B981' },
                                    { name: 'Ocasionales', value: dashboard.occasional_customers, color: '#F59E0B' },
                                    { name: 'Inactivos', value: dashboard.inactive_customers, color: '#EF4444' }
                                  ]}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={90}
                                  paddingAngle={5}
                                  dataKey="value"
                                  label={({ name, value }) => `${value}`}
                                >
                                  {[
                                    { name: 'Frecuentes', value: dashboard.frequent_customers, color: '#10B981' },
                                    { name: 'Ocasionales', value: dashboard.occasional_customers, color: '#F59E0B' },
                                    { name: 'Inactivos', value: dashboard.inactive_customers, color: '#EF4444' }
                                  ].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`${value} clientes`, '']} />
                                <Legend />
                              </RePieChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Top Spenders */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Star className="w-5 h-5 text-amber-500" />
                            Top Clientes por Gasto
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {dashboard.top_spenders?.map((customer, idx) => (
                              <div
                                key={customer.customer_id}
                                className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => { fetchCustomerDetail(customer.customer_id); setActiveTab('customers'); }}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                  idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
                                }`}>
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">{customer.name}</p>
                                  <p className="text-sm text-gray-500">{customer.order_count} pedidos</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">${customer.total_spent?.toLocaleString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Quick Actions */}
                    <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Acciones Rápidas</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Button
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white border-0"
                            onClick={() => { setCustomerFilter('inactive'); setActiveTab('customers'); }}
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Ver Inactivos ({dashboard.inactive_customers})
                          </Button>
                          <Button
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white border-0"
                            onClick={() => setActiveTab('reactivation')}
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Reactivar Clientes
                          </Button>
                          <Button
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white border-0"
                            onClick={() => setActiveTab('products')}
                          >
                            <Package className="w-4 h-4 mr-2" />
                            Ver Productos
                          </Button>
                          <Button
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white border-0"
                            onClick={handleExport}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Exportar Excel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {/* CUSTOMERS TAB */}
              <div style={{ display: activeTab === 'customers' ? 'block' : 'none' }} data-testid="crm-customers-tab">
                {customerDetail ? (
                  <div className="space-y-6">
                    {/* Back Button */}
                    <Button variant="ghost" onClick={() => setCustomerDetail(null)} className="mb-4">
                      <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                      Volver a lista
                    </Button>

                    {/* Customer Detail Header */}
                    <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="bg-white/20 p-4 rounded-full">
                            <Users className="w-10 h-10" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h2 className="text-2xl font-bold">{customerDetail.name}</h2>
                              {/* Cliente Habitual Badge */}
                              {customerDetail.visit_count >= 5 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                                  <Star className="w-3 h-3" /> Cliente Habitual
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-white/90 text-sm">
                              <span className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {customerDetail.phone}
                              </span>
                              {customerDetail.address && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {customerDetail.address}
                                </span>
                              )}
                            </div>
                            {/* Colonia y Cumpleaños en línea separada */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {customerDetail.colonia && (
                                <span className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded text-sm">
                                  <MapPin className="w-3 h-3" /> Col. {customerDetail.colonia}
                                </span>
                              )}
                              {customerDetail.birthday && (
                                <span className="flex items-center gap-1 bg-pink-500/40 px-2 py-1 rounded text-sm">
                                  🎂 Cumpleaños: {customerDetail.birthday}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-3xl font-bold">${customerDetail.total_spent?.toLocaleString()}</p>
                              <p className="text-sm text-white/70">Total Gastado</p>
                            </div>
                            <div>
                              <p className="text-3xl font-bold">{customerDetail.total_orders}</p>
                              <p className="text-sm text-white/70">Pedidos</p>
                            </div>
                            <div>
                              <p className="text-3xl font-bold">
                                ${customerDetail.total_orders > 0 ? Math.round(customerDetail.total_spent / customerDetail.total_orders).toLocaleString() : '0'}
                              </p>
                              <p className="text-sm text-white/70">Ticket Promedio</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Follow-up CRM Panel */}
                    <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2 text-orange-800">
                            <Target className="w-5 h-5" />
                            Seguimiento CRM
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openWhatsApp(customerDetail.phone, customerDetail.name, customerDetail.customer_id)}
                              className="bg-green-500 hover:bg-green-600 text-white border-0"
                              data-testid="whatsapp-btn"
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              WhatsApp
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Current Status Display */}
                        <div className="flex flex-wrap items-center gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Estado actual</Label>
                            <div className="flex items-center gap-2 mt-1">
                              {(() => {
                                const statusInfo = FOLLOW_UP_STATUSES.find(s => s.value === (customerDetail.follow_up_status || 'sin_contactar'));
                                const StatusIcon = statusInfo?.icon || AlertCircle;
                                return (
                                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 ${statusInfo?.color || 'bg-gray-100'}`}>
                                    <StatusIcon className="w-4 h-4" />
                                    {statusInfo?.label || 'Sin Contactar'}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          {customerDetail.last_contact_date && (
                            <div>
                              <Label className="text-xs text-gray-500">Último contacto</Label>
                              <p className="text-sm font-medium mt-1">
                                {new Date(customerDetail.last_contact_date).toLocaleDateString('es-MX', {
                                  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Edit Follow-up Form */}
                        {editingFollowUp ? (
                          <div className="space-y-3 p-3 bg-white rounded-lg border">
                            <div>
                              <Label className="text-sm font-medium">Cambiar Estado</Label>
                              <Select value={followUpStatus} onValueChange={setFollowUpStatus}>
                                <SelectTrigger className="mt-1" data-testid="follow-up-status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {FOLLOW_UP_STATUSES.map(status => (
                                    <SelectItem key={status.value} value={status.value}>
                                      <span className="flex items-center gap-2">
                                        <status.icon className="w-4 h-4" />
                                        {status.label}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Notas de Seguimiento</Label>
                              <textarea
                                value={followUpNotes}
                                onChange={(e) => setFollowUpNotes(e.target.value)}
                                placeholder="Ej: Cliente interesado en promociones de mariscos..."
                                className="w-full mt-1 p-2 border rounded-lg text-sm h-20 resize-none"
                                data-testid="follow-up-notes-input"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleSaveFollowUp}
                                disabled={savingFollowUp}
                                className="bg-orange-600 hover:bg-orange-700"
                                data-testid="save-follow-up-btn"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                {savingFollowUp ? 'Guardando...' : 'Guardar'}
                              </Button>
                              <Button variant="outline" onClick={() => setEditingFollowUp(false)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {customerDetail.follow_up_notes && (
                              <div className="p-3 bg-white rounded-lg border">
                                <Label className="text-xs text-gray-500">Notas</Label>
                                <p className="text-sm mt-1">{customerDetail.follow_up_notes}</p>
                              </div>
                            )}
                            <Button
                              variant="outline"
                              onClick={() => setEditingFollowUp(true)}
                              className="border-orange-300 text-orange-700 hover:bg-orange-100"
                              data-testid="edit-follow-up-btn"
                            >
                              <Target className="w-4 h-4 mr-1" />
                              Actualizar Seguimiento
                            </Button>
                          </div>
                        )}

                        {/* Follow-up History */}
                        {customerDetail.follow_up_history?.length > 0 && (
                          <div>
                            <Label className="text-xs text-gray-500 flex items-center gap-1">
                              <History className="w-3 h-3" /> Historial de Seguimiento
                            </Label>
                            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                              {customerDetail.follow_up_history.slice().reverse().slice(0, 5).map((entry, idx) => {
                                const statusInfo = FOLLOW_UP_STATUSES.find(s => s.value === entry.status);
                                return (
                                  <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-white rounded border">
                                    <span className={`px-2 py-0.5 rounded ${statusInfo?.color || 'bg-gray-100'}`}>
                                      {statusInfo?.label || entry.status}
                                    </span>
                                    <div className="flex-1">
                                      {entry.notes && <p className="text-gray-600">{entry.notes}</p>}
                                      <p className="text-gray-400">
                                        {new Date(entry.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Favorite Products */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Star className="w-5 h-5 text-amber-500" />
                            Productos Favoritos
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {customerDetail.favorite_products?.length > 0 ? (
                            <div className="space-y-2">
                              {customerDetail.favorite_products?.slice(0, 8).map((prod, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <span className="font-medium">{prod.name}</span>
                                  <div className="text-right">
                                    <span className="text-sm text-gray-600">{prod.count}x</span>
                                    <span className="text-sm text-green-600 ml-2">${prod.total?.toFixed(0)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500">
                              <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Sin productos registrados</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Spending Trend */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-blue-600" />
                            Tendencia de Gasto
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {customerDetail.spending_trend?.length > 0 ? (
                            <div className="h-48">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={[...customerDetail.spending_trend].reverse()}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                  <YAxis tick={{ fontSize: 11 }} />
                                  <Tooltip formatter={(v) => [`$${v}`, 'Gasto']} />
                                  <Area type="monotone" dataKey="total" stroke="#2563EB" fill="#BFDBFE" />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500">
                              <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Sin datos de gasto</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Order History */}
                      <Card className="lg:col-span-2">
                        <CardHeader>
                          <CardTitle>Historial de Pedidos ({customerDetail.total_orders || 0})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {customerDetail.order_history?.length > 0 ? (
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                              {customerDetail.order_history?.map((order) => (
                                <div key={order.order_id} className="p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium flex items-center gap-2">
                                        Pedido #{order.order_number}
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          order.order_type === 'domicilio' ? 'bg-blue-100 text-blue-700' :
                                          order.order_type === 'mesa' ? 'bg-green-100 text-green-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {order.order_type === 'domicilio' ? 'Domicilio' :
                                           order.order_type === 'mesa' ? 'Mesa' : 'Mostrador'}
                                        </span>
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {new Date(order.created_at).toLocaleDateString('es-MX', {
                                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                  <p className="font-bold text-green-600">${order.total?.toFixed(2)}</p>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {order.items?.map((item, i) => (
                                    <span key={i} className="text-xs bg-white px-2 py-1 rounded">
                                      {item.qty}x {item.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
                              <p className="font-medium">No se encontraron pedidos</p>
                              <p className="text-sm">Los pedidos del cliente aparecerán aquí</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Filters */}
                    <Card data-testid="crm-customer-filters">
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex-1 min-w-[200px]">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input
                                placeholder="Buscar por nombre o teléfono..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                                data-testid="crm-search-input"
                              />
                            </div>
                          </div>
                          
                          {/* Date Period Filter */}
                          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <Button
                              variant={dateFilter === '7d' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => { setDateFilter('7d'); setShowCustomDates(false); }}
                              className="text-xs px-3"
                              data-testid="date-filter-7d"
                            >
                              7 días
                            </Button>
                            <Button
                              variant={dateFilter === '30d' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => { setDateFilter('30d'); setShowCustomDates(false); }}
                              className="text-xs px-3"
                              data-testid="date-filter-30d"
                            >
                              30 días
                            </Button>
                            <Button
                              variant={dateFilter === '90d' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => { setDateFilter('90d'); setShowCustomDates(false); }}
                              className="text-xs px-3"
                              data-testid="date-filter-90d"
                            >
                              3 meses
                            </Button>
                            <Button
                              variant={dateFilter === 'year' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => { setDateFilter('year'); setShowCustomDates(false); }}
                              className="text-xs px-3"
                              data-testid="date-filter-year"
                            >
                              Año
                            </Button>
                            <Button
                              variant={dateFilter === 'custom' ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => { setDateFilter('custom'); setShowCustomDates(true); }}
                              className="text-xs px-3"
                              data-testid="date-filter-custom"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Personalizado
                            </Button>
                          </div>
                          
                          <Select value={customerFilter} onValueChange={setCustomerFilter}>
                            <SelectTrigger className="w-40" data-testid="crm-filter-select">
                              <Filter className="w-4 h-4 mr-2" />
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              <SelectItem value="frequent">Frecuentes (5+)</SelectItem>
                              <SelectItem value="occasional">Ocasionales</SelectItem>
                              <SelectItem value="inactive">Inactivos (30d+)</SelectItem>
                              <SelectItem value="new">Nuevos (7d)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={originFilter} onValueChange={setOriginFilter}>
                            <SelectTrigger className="w-40" data-testid="crm-origin-filter">
                              <ShoppingBag className="w-4 h-4 mr-2" />
                              <SelectValue placeholder="Origen" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los orígenes</SelectItem>
                              <SelectItem value="dine_in">🍽️ Comedor (Mesas)</SelectItem>
                              <SelectItem value="takeout">🛍️ Mostrador</SelectItem>
                              <SelectItem value="delivery">🛵 Domicilio</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={lastPurchaseFilter} onValueChange={setLastPurchaseFilter}>
                            <SelectTrigger className="w-48" data-testid="crm-last-purchase-filter">
                              <Clock className="w-4 h-4 mr-2" />
                              <SelectValue placeholder="Última compra" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas las compras</SelectItem>
                              <SelectItem value="recent_7d">🟢 Últimos 7 días</SelectItem>
                              <SelectItem value="recent_30d">🟡 Últimos 30 días</SelectItem>
                              <SelectItem value="inactive_30d">🟠 Inactivo +30 días</SelectItem>
                              <SelectItem value="inactive_60d">🔴 Inactivo +60 días</SelectItem>
                              <SelectItem value="never">⚪ Sin compras</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={followUpFilter} onValueChange={setFollowUpFilter}>
                            <SelectTrigger className="w-44" data-testid="crm-follow-up-filter">
                              <Target className="w-4 h-4 mr-2" />
                              <SelectValue placeholder="Estado CRM" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los estados</SelectItem>
                              {FOLLOW_UP_STATUSES.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-44" data-testid="crm-sort-select">
                              <ArrowUpDown className="w-4 h-4 mr-2" />
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="last_visit">Última visita</SelectItem>
                              <SelectItem value="total_spent">Total gastado</SelectItem>
                              <SelectItem value="order_count_period">Pedidos período</SelectItem>
                              <SelectItem value="name">Nombre</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" onClick={handleExport} data-testid="crm-export-btn">
                            <Download className="w-4 h-4 mr-2" />
                            Excel
                          </Button>
                          <Button variant="outline" onClick={handleExportPDF} className="text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="crm-pdf-btn">
                            <FileText className="w-4 h-4 mr-2" />
                            PDF
                          </Button>
                          <Button 
                            onClick={() => setShowNewCustomerModal(true)} 
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid="add-customer-btn"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Nuevo Cliente
                          </Button>
                        </div>
                        
                        {/* Custom Date Range Panel */}
                        {showCustomDates && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium">Desde:</Label>
                                <Input
                                  type="date"
                                  value={customStartDate}
                                  onChange={(e) => setCustomStartDate(e.target.value)}
                                  className="w-40 bg-white"
                                  data-testid="custom-start-date"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium">Hasta:</Label>
                                <Input
                                  type="date"
                                  value={customEndDate}
                                  onChange={(e) => setCustomEndDate(e.target.value)}
                                  className="w-40 bg-white"
                                  data-testid="custom-end-date"
                                />
                              </div>
                              <Button 
                                size="sm" 
                                onClick={fetchCustomers}
                                disabled={!customStartDate || !customEndDate}
                                data-testid="apply-custom-dates"
                              >
                                Aplicar
                              </Button>
                              {customStartDate && customEndDate && (
                                <span className="text-sm text-blue-700 font-medium">
                                  Mostrando: {new Date(customStartDate + 'T12:00:00').toLocaleDateString('es-MX')} - {new Date(customEndDate + 'T12:00:00').toLocaleDateString('es-MX')}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Customer List */}
                    <Card data-testid="crm-customer-list">
                      <CardHeader>
                        <CardTitle>Clientes ({customers.length})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {customers.map((customer) => {
                            const statusInfo = FOLLOW_UP_STATUSES.find(s => s.value === (customer.follow_up_status || 'sin_contactar'));
                            return (
                            <div
                              key={customer.customer_id}
                              className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                              onClick={() => fetchCustomerDetail(customer.customer_id)}
                              data-testid={`crm-customer-row-${customer.customer_id}`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                customer.is_frequent ? 'bg-green-500' :
                                customer.is_inactive ? 'bg-red-400' :
                                customer.is_new ? 'bg-blue-500' : 'bg-amber-500'
                              }`}>
                                {customer.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold truncate">{customer.name}</p>
                                  {customer.is_frequent && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Frecuente</span>}
                                  {customer.is_inactive && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Inactivo</span>}
                                  {customer.is_new && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Nuevo</span>}
                                  {/* Follow-up Status Badge */}
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusInfo?.color || 'bg-gray-100 text-gray-700'}`}>
                                    {statusInfo?.label || 'Sin Contactar'}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 flex items-center gap-3">
                                  <span><Phone className="w-3 h-3 inline mr-1" />{customer.phone}</span>
                                  <span>{customer.order_count_period} pedidos ({days}d)</span>
                                </p>
                                {/* NEW: Last Purchase Info */}
                                {customer.last_purchase && (
                                  <p className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                                    <span className="font-medium">Última compra:</span>
                                    <span>{new Date(customer.last_purchase.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                      customer.last_purchase.origin === 'dine_in' ? 'bg-blue-100 text-blue-700' :
                                      customer.last_purchase.origin === 'takeout' ? 'bg-purple-100 text-purple-700' :
                                      'bg-orange-100 text-orange-700'
                                    }`}>
                                      {customer.last_purchase.origin === 'dine_in' ? '🍽️ Comedor' :
                                       customer.last_purchase.origin === 'takeout' ? '🛍️ Mostrador' : '🛵 Domicilio'}
                                    </span>
                                    <span className="font-medium text-green-600">${customer.last_purchase.total?.toFixed(2)}</span>
                                    {customer.days_since_last_purchase !== null && (
                                      <span className={`text-xs ${
                                        customer.days_since_last_purchase <= 7 ? 'text-green-600' :
                                        customer.days_since_last_purchase <= 30 ? 'text-amber-600' : 'text-red-600'
                                      }`}>
                                        (hace {customer.days_since_last_purchase} días)
                                      </span>
                                    )}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">${customer.total_spent?.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">
                                  Total histórico
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); openWhatsApp(customer.phone, customer.name, customer.customer_id); }}
                                className="text-green-600 hover:bg-green-100"
                                data-testid={`whatsapp-btn-${customer.customer_id}`}
                              >
                                <MessageCircle className="w-5 h-5" />
                              </Button>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                          )})}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {/* PRODUCTS TAB */}
              <div style={{ display: activeTab === 'products' ? 'block' : 'none' }} data-testid="crm-products-tab">
                {productAnalytics && (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-green-600 text-sm">Productos Vendidos</p>
                          <p className="text-2xl font-bold text-green-700">{productAnalytics.total_products_sold}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-blue-600 text-sm">Items Totales</p>
                          <p className="text-2xl font-bold text-blue-700">{productAnalytics.total_items_sold}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-purple-600 text-sm">Ingresos Domicilio</p>
                          <p className="text-2xl font-bold text-purple-700">${productAnalytics.total_revenue?.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-amber-600 text-sm">Sin Pedidos</p>
                          <p className="text-2xl font-bold text-amber-700">{productAnalytics.never_ordered?.length || 0}</p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Top Products Chart */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            Top 10 Productos Domicilio
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={productAnalytics.top_products} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Bar dataKey="total_quantity" fill="#10B981" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Never Ordered */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-amber-600">
                            <Package className="w-5 h-5" />
                            Productos Sin Pedir a Domicilio
                          </CardTitle>
                          <p className="text-sm text-gray-500">Oportunidad de promoción</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {productAnalytics.never_ordered?.map((prod, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                                <div>
                                  <p className="font-medium">{prod.name}</p>
                                  <p className="text-sm text-gray-500">{prod.category}</p>
                                </div>
                                <p className="font-bold">${prod.price?.toFixed(0)}</p>
                              </div>
                            ))}
                            {!productAnalytics.never_ordered?.length && (
                              <p className="text-center text-gray-500 py-4">¡Todos los productos han sido pedidos!</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </div>

              {/* TIMING TAB */}
              <div style={{ display: activeTab === 'timing' ? 'block' : 'none' }} data-testid="crm-timing-tab">
                {hourlyData && (
                  <div className="space-y-6">
                    {/* Peak Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                        <CardContent className="p-5">
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Horarios Pico
                          </h3>
                          <div className="space-y-2">
                            {hourlyData.peak_hours?.map((h, i) => (
                              <div key={i} className="flex items-center justify-between bg-white/20 rounded-lg p-2">
                                <span className="font-medium">{h.label}</span>
                                <span>{h.orders} pedidos</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                        <CardContent className="p-5">
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Días con Más Pedidos
                          </h3>
                          <div className="space-y-2">
                            {hourlyData.peak_days?.map((d, i) => (
                              <div key={i} className="flex items-center justify-between bg-white/20 rounded-lg p-2">
                                <span className="font-medium">{d.day}</span>
                                <span>{d.orders} pedidos</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Hourly Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Distribución por Hora</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hourlyData.hourly_distribution}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="orders" fill="#2563EB" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Daily Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Distribución por Día</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hourlyData.daily_distribution}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="day" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="orders" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {/* REACTIVATION TAB */}
              <div style={{ display: activeTab === 'reactivation' ? 'block' : 'none' }} data-testid="crm-reactivation-tab">
                {reactivation && (
                  <div className="space-y-6">
                    <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="bg-white/20 p-3 rounded-full">
                            <Zap className="w-8 h-8" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">Clientes para Reactivar</h2>
                            <p className="text-white/80">
                              {reactivation.total} clientes sin pedir en los últimos {reactivation.days_inactive_threshold} días
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="space-y-4" data-testid="crm-reactivation-list">
                      {reactivation.customers?.map((customer) => (
                        <Card key={customer.customer_id} className="hover:shadow-lg transition-shadow" data-testid={`crm-reactivation-card-${customer.customer_id}`}>
                          <CardContent className="p-5">
                            <div className="flex flex-col md:flex-row md:items-center gap-4">
                              <div className="flex items-center gap-3 flex-1">
                                <div className="bg-red-100 p-3 rounded-full">
                                  <UserX className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-lg">{customer.name}</p>
                                  <p className="text-gray-500 flex items-center gap-2">
                                    <Phone className="w-4 h-4" />
                                    {customer.phone}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <p className="text-xl font-bold text-gray-700">{customer.days_since_last_order}</p>
                                  <p className="text-xs text-gray-500">Días sin pedir</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-green-600">${customer.total_spent?.toLocaleString()}</p>
                                  <p className="text-xs text-gray-500">Total histórico</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-blue-600">{customer.visit_count}</p>
                                  <p className="text-xs text-gray-500">Visitas</p>
                                </div>
                              </div>
                            </div>

                            {customer.favorite_products?.length > 0 && (
                              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                                <p className="text-sm font-medium text-amber-800 mb-2">
                                  <Star className="w-4 h-4 inline mr-1" />
                                  Productos favoritos:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {customer.favorite_products.map((p, i) => (
                                    <span key={i} className="px-2 py-1 bg-white rounded text-sm">
                                      {p.name} ({p.count}x)
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-testid="crm-suggested-message">
                              <p className="text-sm text-blue-800">
                                <MessageCircle className="w-4 h-4 inline mr-1" />
                                <strong>Mensaje sugerido:</strong> {customer.suggested_message}
                              </p>
                            </div>

                            <div className="mt-4 flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => window.open(`https://wa.me/52${customer.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(customer.suggested_message)}`, '_blank')}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`crm-whatsapp-btn-${customer.customer_id}`}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" />
                                Enviar WhatsApp
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { fetchCustomerDetail(customer.customer_id); setActiveTab('customers'); }}
                                data-testid={`crm-view-detail-btn-${customer.customer_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver Detalle
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      
      {/* Modal: Nuevo Cliente */}
      {showNewCustomerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-600" />
                Nuevo Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  placeholder="Nombre completo"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  data-testid="new-customer-name"
                />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input
                  placeholder="10 dígitos"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  data-testid="new-customer-phone"
                />
              </div>
              <div>
                <Label>Dirección</Label>
                <Input
                  placeholder="Calle y número"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                />
              </div>
              <div>
                <Label>Colonia</Label>
                <Input
                  placeholder="Colonia"
                  value={newCustomer.colonia}
                  onChange={(e) => setNewCustomer({ ...newCustomer, colonia: e.target.value })}
                />
              </div>
              <div>
                <Label>Cumpleaños</Label>
                <Input
                  type="date"
                  value={newCustomer.birthday}
                  onChange={(e) => setNewCustomer({ ...newCustomer, birthday: e.target.value })}
                />
              </div>
              <div>
                <Label>Notas</Label>
                <Input
                  placeholder="Notas adicionales (alergias, preferencias...)"
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNewCustomerModal(false);
                    setNewCustomer({ name: '', phone: '', address: '', colonia: '', birthday: '', notes: '' });
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateCustomer}
                  disabled={savingCustomer || !newCustomer.name.trim() || !newCustomer.phone.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="save-new-customer-btn"
                >
                  {savingCustomer ? 'Guardando...' : 'Guardar Cliente'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DeliveryCRM;
